import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Plus, Download, Edit, Trash, LogIn } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Segment } from '@/api/entities';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from '@/components/utils/translations';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';

export default function Segments({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const isAuthenticated = !!contextUser?.id;
  const effectiveTenant = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );

  useEffect(() => {
    if (!contextUser) setAuthError(true);
    else setAuthError(false);
  }, [contextUser]);

  const loadSegments = useCallback(async () => {
    if (!contextUser?.id) return;
    setIsLoading(true);
    try {
      const userSegments = await Segment.filter({ 
        tenant_id: effectiveTenant,
        user_id: contextUser.id 
      });
  
      setSegments(userSegments);
    } catch (error) {
      console.error('Failed to load segments:', error);
      toast({ variant: 'destructive', title: 'Failed to load segments' });
    }
    setIsLoading(false);
  }, [toast, currentTenant, contextUser, effectiveTenant]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSegments();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, loadSegments]);

  const handleLogin = async () => {
    try {
      const { User } = await import('@/api/entities');
      await User.loginWithRedirect(window.location.href);
    } catch (error) {
      toast({ variant: "destructive", title: "Login Failed" });
    }
  };
  
  const handleCardClick = (seg) => {
    navigate(createPageUrl(`SegmentResults?id=${seg.id}&tenant_id=${seg.tenant_id || 'instructure'}`));
  };

  const handleDeleteClick = (segment, e) => {
    e.stopPropagation();
    setSegmentToDelete(segment);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!segmentToDelete) return;
    
    setIsDeleting(true);
    try {
        // Verify user owns this segment
        if (segmentToDelete.user_id && segmentToDelete.user_id !== contextUser?.id) {
            toast({ 
                variant: "destructive", 
                title: "Access Denied", 
                description: "You don't have permission to delete this segment." 
            });
            setDeleteDialogOpen(false);
            setSegmentToDelete(null);
            return;
        }
        
        await Segment.delete(segmentToDelete.id);
        toast({ title: "Segment deleted successfully" });
        setDeleteDialogOpen(false);
        setSegmentToDelete(null);
        await loadSegments();
    } catch (error) {
        console.error('Delete error:', error);
        toast({ variant: "destructive", title: "Failed to delete segment", description: error.message });
    } finally {
        setIsDeleting(false);
    }
};

  if (authError && !isAuthenticated) {
    return (
      <div className="text-center py-20">
        <LogIn className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-[#0A1A2F]">{t('authRequired')}</h2>
        <p className="text-[#5C6B7A] mb-6">{t('loginToAccess')}</p>
        <Button onClick={handleLogin} className="bg-[#00C2C2] hover:bg-[#00A3A3]">{t('logIn')}</Button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 -m-8 -mt-8 min-h-screen">
      <div className="bg-white border-b mb-6 -mx-8">
        <div className="max-w-7xl mx-auto px-12 sm:px-8 md:px-10 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-[#0A1A2F]">{t('segments')}</h1>
              <p className="text-lg text-[#5C6B7A]">{t('createManageExport')}</p>
            </div>
            {isAuthenticated && (
              <Link to={createPageUrl('SegmentBuilder')}>
                <Button className="bg-[#00C2C2] hover:bg-[#00A3A3] text-sm md:text-base h-9 md:h-10">
                  <Plus className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                  {t('buildNewSegment')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 py-0">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {isLoading ? (
            [...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 shadow-md"><CardHeader><Skeleton className="h-5 w-3/4" /></CardHeader><CardContent><Skeleton className="h-4 w-1/2" /><Skeleton className="h-4 w-1/3 mt-2" /></CardContent></Card>
            ))
          ) : segments.map(seg => (
            <Card key={seg.id} className="border-0 shadow-md hover:shadow-xl transition-shadow cursor-pointer" onClick={() => handleCardClick(seg)}>
              <CardHeader className="pb-3 px-4 pt-4">
                <CardTitle className="text-sm md:text-lg font-semibold text-[#0A1A2F]">{seg.name}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="flex items-center gap-2 text-xs md:text-sm text-[#5C6B7A]">
                  <Users className="w-3 h-3 md:w-4 md:h-4" />
                  <span>{seg.rules?.length || 0} {t('rules')}</span>
                </div>
                <p className="text-[10px] md:text-xs text-gray-400 mt-1">{t('created')}: {new Date(seg.created_at).toLocaleDateString()}</p>
                <div className="mt-3 md:mt-4 flex justify-end gap-2">
                  <Link
                    to={createPageUrl(
                      `SegmentResults?id=${seg.id}&tenant_id=${encodeURIComponent(seg.tenant_id || effectiveTenant)}`
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="outline" size="sm" className="text-xs h-7"><Download className="w-3 h-3 mr-1" /> {t('viewResults')}</Button>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={(e) => handleDeleteClick(seg, e)} className="text-red-500 hover:bg-red-100 hover:text-red-600 h-7 w-7">
                    <Trash className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {!isLoading && segments.length === 0 && (
          <div className="text-center py-12 md:py-20 border-2 border-dashed rounded-lg">
            <Users className="w-10 h-10 md:w-12 md:h-12 mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg md:text-xl font-semibold text-[#0A1A2F]">{t('noSegmentsYet')}</h2>
            <p className="text-sm md:text-base text-[#5C6B7A] mb-4 md:mb-6">{t('getStartedSegment')}</p>
            <Link to={createPageUrl('SegmentBuilder')}>
              <Button className="bg-[#00C2C2] hover:bg-[#00A3A3] text-sm md:text-base h-9 md:h-10">
                <Plus className="w-3 h-3 md:w-4 md:h-4 mr-2" />
                {t('buildNewSegment')}
              </Button>
            </Link>
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteSegment')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteSegmentConfirm')} "{segmentToDelete?.name}"? {t('cannotUndo')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? t('loading') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}